import { PageHeader, Badge } from '../../shared/ui'

interface ComplianceItem {
  id: string
  title: string
  law: string
  status: 'active' | 'pending' | 'warning' | 'inactive'
  priority: 'hoch' | 'mittel' | 'niedrig'
  deadline?: string
  owner: string
  note: string
}

const complianceItems: ComplianceItem[] = [
  {
    id: 'nis2-registration',
    title: 'BSI-Registrierung (NIS2)',
    law: 'NIS2UmsG § 33',
    status: 'warning',
    priority: 'hoch',
    deadline: '2026-03-06',
    owner: 'IT.Admin',
    note: 'Frist war 06.03.2026 — sofortige Nachregistrierung nötig',
  },
  {
    id: 'nis2-lawyer',
    title: 'Fachanwalt: Brancheneinstufung prüfen',
    law: 'NIS2UmsG Anlage 2 Nr. 8',
    status: 'pending',
    priority: 'hoch',
    owner: 'Geschäftsführung',
    note: 'Ernährungssektor: "wichtige Einrichtung" vs. "besonders wichtige Einrichtung"',
  },
  {
    id: 'pca-avv',
    title: 'AVV mit PCA (Auftragsverarbeitung)',
    law: 'DSGVO Art. 28',
    status: 'pending',
    priority: 'hoch',
    owner: 'IT.Admin',
    note: 'PCA verarbeitet Schülerdaten — AVV anfordern',
  },
  {
    id: 'dsb',
    title: 'Datenschutzbeauftragter bestellt',
    law: 'DSGVO Art. 37 / § 38 BDSG',
    status: 'active',
    priority: 'hoch',
    owner: 'DSB',
    note: 'DSB ist bestellt ✅',
  },
  {
    id: 'vvt',
    title: 'Verzeichnis der Verarbeitungstätigkeiten',
    law: 'DSGVO Art. 30',
    status: 'pending',
    priority: 'hoch',
    owner: 'DSB',
    note: '5 Systeme: PCA, DIVIS, SAP, FileMaker ERP, Zeiterfassung',
  },
  {
    id: 'tse',
    title: 'TSE (Kassensicherungsverordnung)',
    law: 'KassenSichV / AO § 146a',
    status: 'pending',
    priority: 'hoch',
    owner: 'IT.Admin',
    note: 'TSE-Status an allen 112 SAP-Kiosksystemen prüfen',
  },
  {
    id: 'isms',
    title: 'ISMS / IT-Sicherheitsrichtlinie',
    law: 'NIS2UmsG § 30',
    status: 'inactive',
    priority: 'mittel',
    owner: 'IT.Admin',
    note: 'BSI Standard-Absicherung erforderlich (nicht nur Basis)',
  },
  {
    id: 'backup',
    title: 'Backup-Konzept (3-2-1)',
    law: 'BSI CON.3',
    status: 'pending',
    priority: 'mittel',
    owner: 'IT.Admin',
    note: '3 Kopien, 2 Medien, 1 offsite — Windows Server prüfen',
  },
  {
    id: 'mfa',
    title: 'Multi-Faktor-Authentifizierung',
    law: 'NIS2UmsG § 30 Abs. 2',
    status: 'pending',
    priority: 'mittel',
    owner: 'IT.Admin',
    note: 'MFA für alle sensitiven Systeme (FileMaker HR, DIVIS-API)',
  },
  {
    id: 'joint-controller',
    title: 'Joint Controller Agreement (Art. 26)',
    law: 'DSGVO Art. 26',
    status: 'inactive',
    priority: 'mittel',
    owner: 'DSB',
    note: 'Beide GmbHs teilen Windows Server + FileMaker — JCA erforderlich',
  },
  {
    id: 'website-dse',
    title: 'Datenschutzerklärung mammascanteen.de',
    law: 'DSGVO Art. 13',
    status: 'warning',
    priority: 'niedrig',
    owner: 'DSB',
    note: 'Deckt nur Website ab — operativer Betrieb fehlt noch',
  },
  {
    id: 'gf-training',
    title: 'Geschäftsführer IT-Sicherheitsschulung',
    law: 'NIS2UmsG § 38 Abs. 3',
    status: 'inactive',
    priority: 'niedrig',
    owner: 'Geschäftsführung',
    note: 'Fortbildungspflicht — persönliche Haftung der GF bei Verstoß',
  },
]

const priorityOrder = { hoch: 0, mittel: 1, niedrig: 2 }

const statusTone = (s: ComplianceItem['status']): 'success' | 'warn' | 'danger' | 'neutral' => {
  switch (s) {
    case 'active': return 'success'
    case 'pending': return 'warn'
    case 'warning': return 'danger'
    case 'inactive': return 'neutral'
    default: return 'neutral'
  }
}

const statusLabel = (s: ComplianceItem['status']) => {
  switch (s) {
    case 'active': return 'Erledigt'
    case 'pending': return 'Ausstehend'
    case 'warning': return 'Dringend'
    case 'inactive': return 'Nicht begonnen'
  }
}

export default function ITCompliance() {
  const sorted = [...complianceItems].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  )

  const done = complianceItems.filter((i) => i.status === 'active').length
  const urgent = complianceItems.filter((i) => i.status === 'warning').length
  const pending = complianceItems.filter((i) => i.status === 'pending' || i.status === 'inactive').length

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        title={`Compliance — ${done} / ${done + urgent + pending} erledigt`}
        subtitle="IT-Compliance-Tracker — NIS2, DSGVO, BSI und KassenSichV"
      />

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
          <p className="text-[11px] font-bold text-teal-700 uppercase tracking-wide">Erledigt</p>
          <p className="font-headline font-bold text-3xl text-teal-700 mt-1">{done}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <p className="text-[11px] font-bold text-red-700 uppercase tracking-wide">Dringend</p>
          <p className="font-headline font-bold text-3xl text-red-700 mt-1">{urgent}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">Ausstehend</p>
          <p className="font-headline font-bold text-3xl text-amber-700 mt-1">{pending}</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mb-6 p-4 bg-surface-container-low rounded-xl border border-slate-200 flex items-start gap-3">
        <span className="material-symbols-outlined text-on-surface-variant mt-0.5 text-base">info</span>
        <p className="text-xs text-on-surface-variant leading-relaxed">
          Basiert auf dem IT-Compliance-Bericht (April 2026). Rechtliche Einschätzungen zur NIS2-Einstufung
          sollten durch einen IT-Fachanwalt bestätigt werden. Dieses Tool dient der internen
          Fortschrittsverfolgung — kein Rechtsersatz.
        </p>
      </div>

      {/* Items table */}
      <div className="bg-surface-container-lowest border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-surface-container-low">
              <th className="text-left px-4 py-3 text-[11px] font-bold text-on-surface-variant uppercase tracking-wide">
                Maßnahme
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-on-surface-variant uppercase tracking-wide">
                Rechtsgrundlage
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-on-surface-variant uppercase tracking-wide">
                Priorität
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-on-surface-variant uppercase tracking-wide">
                Status
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-on-surface-variant uppercase tracking-wide">
                Zuständig
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((item, idx) => (
              <tr
                key={item.id}
                className={`border-b border-slate-100 last:border-0 ${idx % 2 === 0 ? '' : 'bg-surface-container-low/40'}`}
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-on-surface">{item.title}</p>
                  <p className="text-xs text-on-surface-variant mt-0.5 leading-snug">{item.note}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-mono text-on-surface-variant">{item.law}</span>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={item.priority === 'hoch' ? 'danger' : item.priority === 'mittel' ? 'warn' : 'neutral'}>
                    {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-on-surface-variant">{item.owner}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-outline mt-4">
        Quelle: IT-Compliance Gesamtanalyse · IT-Manager Aktionsplan · April 2026
      </p>
    </div>
  )
}
